import type { ReactElement } from 'react';
import { Link } from '@tanstack/react-router';
import { useModerationCandidatesPreview, type ModerationCandidate } from '@/lib/queries/admin';
import { AdminCardError } from '@/components/atoms/admin-card-error';

const PREVIEW_LIMIT = 6;

export function AdminQueuePreviewCard(): ReactElement {
  const { data, isError } = useModerationCandidatesPreview();

  if (isError) {
    return <AdminCardError label="moderation queue" />;
  }

  if (!data) {
    return <CardShell totalCount={null}><SkeletonRows count={3} /></CardShell>;
  }

  const candidates = data.content.slice(0, PREVIEW_LIMIT);

  return (
    <CardShell totalCount={data.totalElements}>
      {candidates.length === 0 ? (
        <EmptyRow message="Queue is clear" />
      ) : (
        <ul className="divide-y divide-[var(--rule)]">
          {candidates.map((c) => (
            <li key={c.pieceId}>
              <CandidateRow candidate={c} />
            </li>
          ))}
        </ul>
      )}
    </CardShell>
  );
}

function CardShell({
  totalCount, children,
}: { totalCount: number | null; children: React.ReactNode }): ReactElement {
  return (
    <section className="rounded-[var(--r-md)] border border-[var(--rule)] bg-[var(--bg-surface)] overflow-hidden">
      <header className="px-4 py-3 flex items-center justify-between border-b border-[var(--rule)]">
        <h2 className="text-[13px] font-semibold">
          Moderation queue{totalCount !== null && ` · ${totalCount}`}
        </h2>
        <Link to="/admin/queue" className="text-[12px] text-[var(--brand)] hover:underline">
          Open queue →
        </Link>
      </header>
      {children}
    </section>
  );
}

function CandidateRow({ candidate }: { candidate: ModerationCandidate }): ReactElement {
  return (
    <Link
      to="/admin/queue/$pieceId"
      params={{ pieceId: candidate.pieceId }}
      className="grid grid-cols-[1fr_auto] gap-4 items-center px-4 py-2.5 hover:bg-[var(--bg-soft)]"
    >
      <span className="text-[13px] font-medium truncate">{candidate.prettyName}</span>
      <span className="text-[11px] text-[var(--ink-3)]">{candidate.submitterHandle ?? '—'}</span>
    </Link>
  );
}

function EmptyRow({ message }: { message: string }): ReactElement {
  return (
    <div className="px-4 py-6 text-[12px] text-[var(--ink-3)] text-center">{message}</div>
  );
}

function SkeletonRows({ count }: { count: number }): ReactElement {
  return (
    <ul className="divide-y divide-[var(--rule)]">
      {Array.from({ length: count }).map((_, i) => (
        <li key={i} className="px-4 py-3">
          <div className="h-3 w-1/2 rounded bg-[var(--bg-soft)]" />
        </li>
      ))}
    </ul>
  );
}
